import { ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { ElNotification } from 'element-plus'
import { useSessionStore } from '../auth/session'

interface AlertEvent {
  eventId: string
  type: 'alert.opened' | 'alert.resolved'
  alertId: string
  severity: 'info' | 'warning' | 'critical'
  title: string
  occurredAt: string
}

export const useEventStreamStore = defineStore('event-stream', () => {
  const session = useSessionStore()
  const unreadAlerts = ref(0)
  const connected = ref(false)
  let source: EventSource | undefined

  function close(): void {
    source?.close()
    source = undefined
    connected.value = false
  }

  function handle(event: MessageEvent<string>): void {
    try {
      const payload = JSON.parse(event.data) as AlertEvent
      if (payload.type === 'alert.opened') {
        unreadAlerts.value += 1
        ElNotification({
          title: payload.severity === 'critical' ? '严重告警' : '新告警',
          message: payload.title,
          type: payload.severity === 'critical' ? 'error' : 'warning',
          duration: 8000,
        })
      } else {
        ElNotification({ title: '告警已恢复', message: payload.title, type: 'success', duration: 5000 })
      }
    } catch {
      // Invalid events are ignored; the alert center remains the authoritative fallback.
    }
  }

  function connect(): void {
    close()
    if (!session.authenticated || typeof EventSource === 'undefined') return
    source = new EventSource('/api/v1/events/stream', { withCredentials: true })
    source.addEventListener('open', () => { connected.value = true })
    source.addEventListener('error', () => { connected.value = false })
    source.addEventListener('alert.opened', handle as EventListener)
    source.addEventListener('alert.resolved', handle as EventListener)
  }

  function markAlertsRead(): void { unreadAlerts.value = 0 }

  watch(() => session.authenticated, (authenticated) => {
    if (authenticated) connect()
    else close()
  }, { immediate: true })

  return { unreadAlerts, connected, connect, close, markAlertsRead }
})
